import { useMemo, memo } from 'react'
import { getVaultPosition, VaultPosition } from 'utils/cakePool'

import { Flex, Text, Box, TooltipText, useTooltip, HelpIcon, BalanceWithLoading } from '@pancakeswap/uikit'
import { LightGreyCard } from 'components/Card'
import { useTranslation } from '@pancakeswap/localization'
import { useVaultApy } from 'hooks/useVaultApy'
import Divider from 'components/Divider'
import isUndefinedOrNull from '@pancakeswap/utils/isUndefinedOrNull'
import { getBalanceNumber, getFullDisplayBalance } from '@pancakeswap/utils/formatBalance'
import { DeserializedPool, VaultKey } from '@pancakeswap/uikit/src/widgets/Pool'
import { Token } from '@pancakeswap/sdk'
import { DeserializedLockedVaultUser, IsCakeVault } from 'state/types'
// import { useAccount } from 'wagmi'
import { getCakeVaultProfit } from 'views/Pools/helpers'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useBUSDPrice from 'hooks/useBUSDPrice'
import BurningCountDown from './Common/BurningCountDown'
import LockedActions from './Common/LockedActions'
import YieldBoostRow from './Common/YieldBoostRow'
import LockDurationRow from './Common/LockDurationRow'
import IfoCakeRow from './Common/IfoCakeRow'
import useUserDataInVaultPresenter from './hooks/useUserDataInVaultPresenter'
import { LockedStakingApyPropsType } from './types'
import LockedAprTooltipContent from './Common/LockedAprTooltipContent'

interface LockedStakingApyProps extends LockedStakingApyPropsType {
  pool: DeserializedPool<Token>,
  showICake?: boolean
}

const LockedStakingApy: React.FC<React.PropsWithChildren<LockedStakingApyProps>> = ({
  pool,
  stakingToken,
  stakingTokenBalance,
  userData,
  showICake,
}) => {
  const { t } = useTranslation()
  const position = useMemo(
    () =>
      getVaultPosition({
        userShares: userData?.userShares,
        locked: userData?.locked,
        lockEndTime: userData?.lockEndTime,
      }),
    [userData],
  )

  const { stakingTokenPrice } = pool
  const stakingTokenPriceUSD = useBUSDPrice(stakingToken)
  
  const { account, chainId } = useActiveWeb3React()

  const currentLockedAmountAsBigNumber = useMemo(() => {
    if (pool.vaultKey === VaultKey.CakeVault)
      return userData?.balance?.cakeAsBigNumber
    if (pool.vaultKey === VaultKey.PldVault)
      return userData?.lastUserActionAmount.times(1e18)
    return userData?.lastUserActionAmount
  }, [pool.vaultKey, userData?.balance?.cakeAsBigNumber, userData?.lastUserActionAmount])

  const currentLockedAmount = getBalanceNumber(currentLockedAmountAsBigNumber)

  const usdValueStaked = currentLockedAmount * (stakingTokenPrice ?? parseFloat(stakingTokenPriceUSD?.toFixed(stakingToken.decimals)))

  const { weekDuration, lockEndDate, secondDuration, remainingTime, burnStartTime } = useUserDataInVaultPresenter({
    lockStartTime: userData?.lockStartTime,
    lockEndTime: userData?.lockEndTime,
    burnStartTime: userData?.burnStartTime,
  })

  const { lockedApy } = useVaultApy({ chainId, vaultKey: pool.vaultKey, duration: secondDuration })

  const isCakeVault = IsCakeVault(pool.vaultKey)
  const { autoCakeToDisplay } = getCakeVaultProfit(
    account,
    userData.pendingProfit,
    pool.earningTokenPrice,
    isCakeVault
      ? (userData as DeserializedLockedVaultUser).currentPerformanceFee?.plus(
        (userData as DeserializedLockedVaultUser).currentOverdueFee,
      )
      : null,
  )

  const boostedYieldAmount = useMemo(() => {
    return getFullDisplayBalance(userData?.lastUserActionAmount, 18, 5)
  }, [userData?.lastUserActionAmount])

  const tooltipContent = <LockedAprTooltipContent boostedYieldAmount={boostedYieldAmount} />
  const { targetRef, tooltip, tooltipVisible } = useTooltip(tooltipContent, { placement: 'bottom-start' })

  const tooltipContentOfBurn = t(
    'After Burning starts at %burnStartTime%. You need to renew your fix-term position, to initiate a new lock or convert your staking position to flexible before it starts. Otherwise all the rewards will be burned within the next 90 days.',
    { burnStartTime },
  )
  const {
    targetRef: tagTargetRefOfBurn,
    tooltip: tagTooltipOfBurn,
    tooltipVisible: tagTooltipVisibleOfBurn,
  } = useTooltip(tooltipContentOfBurn, {
    placement: 'bottom',
  })

  return (
    <LightGreyCard>
      <Flex justifyContent="space-between" mb="16px">
        <Box>
          <Text color="textSubtle" textTransform="uppercase" bold fontSize="12px">
            {t('%symbol% locked', { symbol: stakingToken.symbol })}
          </Text>
          <BalanceWithLoading color="text" bold fontSize="16px" value={currentLockedAmount} decimals={2} />
          <BalanceWithLoading
            value={usdValueStaked}
            fontSize="12px"
            color="textSubtle"
            decimals={2}
            prefix="~"
            unit=" USD"
          />
        </Box>
        <Box>
          <Text color="textSubtle" textTransform="uppercase" bold fontSize="12px">
            {t('Unlocks In')}
          </Text>
          <Flex alignItems="center">
            <Text color={position >= VaultPosition.LockedEnd ? '#D67E0A' : 'text'} bold fontSize="16px">
              {position >= VaultPosition.LockedEnd ? t('Unlocked') : remainingTime}
              {tagTooltipVisibleOfBurn && tagTooltipOfBurn}
            </Text>
            <span ref={tagTargetRefOfBurn}>
              <HelpIcon ml="4px" width="20px" height="20px" color="textSubtle" />
            </span>
          </Flex>
          <Text color={position >= VaultPosition.LockedEnd ? '#D67E0A' : 'text'} fontSize="12px">
            {t('On %date%', { date: lockEndDate })}
          </Text>
        </Box>
      </Flex>
      <Box mb="16px">
        <LockedActions
          pool={pool}
          userShares={userData?.userShares}
          locked={userData?.locked}
          lockEndTime={userData?.lockEndTime}
          lockStartTime={userData?.lockStartTime}
          stakingToken={stakingToken}
          stakingTokenBalance={stakingTokenBalance}
          lockedAmount={currentLockedAmountAsBigNumber}
        />
      </Box>
      <Divider />
      {![VaultPosition.LockedEnd, VaultPosition.AfterBurning].includes(position) && (
        <Flex alignItems="center" justifyContent="space-between">
          {tooltipVisible && tooltip}
          <TooltipText>
            <Text ref={targetRef} color="textSubtle" textTransform="uppercase" bold fontSize="12px">
              {t('APR')}
            </Text>
          </TooltipText>
          <BalanceWithLoading color="text" bold fontSize="16px" value={parseFloat(lockedApy)} decimals={2} unit="%" />
        </Flex>
      )}
      <LockDurationRow weekDuration={weekDuration} />
      {![VaultPosition.LockedEnd, VaultPosition.AfterBurning].includes(position) && (
        <YieldBoostRow secondDuration={secondDuration} vaultKey={pool.vaultKey} />
      )}
      <Flex alignItems="center" justifyContent="space-between">
        <Text color="textSubtle" textTransform="uppercase" bold fontSize="12px">
          {t('Recent BBC profit')}
        </Text>
        <BalanceWithLoading color="primary" bold fontSize="18px" value={autoCakeToDisplay} decimals={2} />
      </Flex>
      {position === VaultPosition.LockedEnd && (
        <Flex alignItems="center" justifyContent="space-between">
          <Text color="textSubtle" textTransform="uppercase" bold fontSize="12px">
            {t('After Burning In')}
          </Text>
          <Text color="failure" bold>
            <BurningCountDown lockEndTime={userData?.lockEndTime} />
          </Text>
        </Flex>
      )}
      {position === VaultPosition.AfterBurning && (
        <Flex alignItems="center" justifyContent="space-between">
          <Text color="textSubtle" textTransform="uppercase" bold fontSize="12px">
            {t('After burning')}
          </Text>
          <Text color="failure" bold>
            {isUndefinedOrNull(userData?.currentOverdueFee)
              ? '-'
              : t('%amount% Burned', { amount: getFullDisplayBalance(userData?.currentOverdueFee, 18, 5) })}
          </Text>
        </Flex>
      )}
      {showICake && <IfoCakeRow />}
    </LightGreyCard>
  )
}

export default memo(LockedStakingApy)
